import type { CSSProperties, ReactNode } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const SIZES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-5xl',
  full: 'sm:max-w-6xl',
} as const;

export type RecordDialogSize = keyof typeof SIZES;

type Props = {
  open: boolean;
  /** Escape, the X, a click outside and Close all come here; with useUrlRecord, `record.close`. */
  onClose: () => void;
  title: ReactNode;
  /** Shown under the title; a screen reader's description too. */
  description?: ReactNode;
  /** Under the description, still in the fixed header (a column chooser, a summary line). */
  header?: ReactNode;
  /** A toolbar between the header and the body (search, filters); stays put while the body scrolls. */
  toolbar?: ReactNode;
  /** The buttons at the bottom (Cancel, Save), right-aligned. Stays put while the body scrolls. */
  footer?: ReactNode;
  /** sm 28rem (a short panel), md 42rem, lg 48rem (a record's fields, a list), xl 64rem (a table), full 72rem (an editor). */
  size?: RecordDialogSize;
  /** A fixed height (88% of the window), so a table loading or filtering doesn't make it jump. */
  tall?: boolean;
  /** The body has no padding (a table that fills it); its header sticks to the top of the body. */
  flush?: boolean;
  /** What couldn't be shown if the body crashes: "Couldn't show this ad set". */
  errorTitle?: string;
  /** The record shown, so a crash clears when another one opens. */
  recordKey?: string | null;
  bodyClassName?: string;
  children: ReactNode;
};

/**
 * A record over its page, in a centred modal: a fixed header, a body that
 * scrolls, an optional fixed toolbar and footer. On a phone it fills the screen. Give it a URL with
 * `useUrlRecord` (`?adset=<id>`) so the record can be linked, refreshed and closed with Back.
 */
export function RecordDialog({
  open,
  onClose,
  title,
  description,
  header,
  toolbar,
  footer,
  size = 'lg',
  tall = false,
  flush = false,
  errorTitle = "Couldn't show this",
  recordKey,
  bodyClassName,
  children,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className={cn(
          'flex flex-col gap-0 overflow-hidden p-0',
          'max-h-[calc(100dvh-2rem)] sm:max-h-[90dvh]',
          SIZES[size],
          tall && 'sm:h-[88dvh]',
          // Phones get the whole screen, as a task does.
          'max-sm:inset-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0'
        )}
        // Focus goes to the dialog itself, not its first field, so a phone's keyboard stays down and no
        // tooltip opens itself, while a screen reader is still taken into the dialog.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
        }}
        {...(description ? {} : { 'aria-describedby': undefined })}
      >
        <DialogHeader className="shrink-0 gap-1 border-b px-4 py-3 pr-12 text-left sm:px-6 sm:py-4 sm:pr-12">
          <DialogTitle className="text-base leading-snug">{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
          {header}
        </DialogHeader>
        {toolbar ? <div className="shrink-0 border-b px-4 py-2 sm:px-6">{toolbar}</div> : null}
        <div
          className={cn('min-h-0 flex-1 overflow-y-auto', flush ? 'overflow-x-auto' : 'p-4 sm:px-6', bodyClassName)}
          // The body scrolls itself, so a table's header sticks to the body's top.
          style={flush ? ({ '--data-table-sticky-top': '0px' } as CSSProperties) : undefined}
        >
          <ErrorBoundary
            variant="section"
            title={errorTitle}
            resetKeys={[recordKey]}
            className={flush ? 'p-4' : undefined}
          >
            {children}
          </ErrorBoundary>
        </div>
        {footer ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t px-4 py-3 sm:px-6">
            {footer}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
